import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Input, message, Space, Typography, Tag } from 'antd';
import axios from 'axios';
import DOMPurify from 'dompurify';

const { TextArea } = Input;
const { Text, Title } = Typography;

const TasksPage = () => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [modifiedContent, setModifiedContent] = useState('');
    const [comments, setComments] = useState('');

    useEffect(() => {
        fetchTasks();
        // Refresh tasks every 30 seconds
        const interval = setInterval(fetchTasks, 30000);
        return () => clearInterval(interval);
    }, []);

    const fetchTasks = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${window.wpAiWorkflowsSettings.root}wp-ai-workflows/v1/human-tasks`,
                { headers: { 'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce } }
            );
            setTasks(response.data);
        } catch (error) {
            console.error('Error fetching tasks:', error);
            message.error('Failed to fetch tasks');
        }
        setLoading(false);
    };

    const handleTaskAction = async (task, action) => {
        try {
            const contentToSend = action === 'modify' ? modifiedContent : selectedTask.content;
            await axios.post(
                `${window.wpAiWorkflowsSettings.root}wp-ai-workflows/v1/human-tasks/${task.id}/${action}`,
                {
                    modified_content: contentToSend,
                    comments: comments
                },
                {
                    headers: { 
                        'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce,
                        'Content-Type': 'application/json'
                    }
                }
            );
            message.success(`Task ${action}ed successfully`);
            setModalVisible(false);
            fetchTasks();
        } catch (error) {
            console.error(`Error ${action}ing task:`, error);
            message.error(`Failed to ${action} task`);
        }
    };

    const columns = [
        {
            title: 'Workflow',
            dataIndex: 'workflow_name',
            key: 'workflow_name'
        },
        {
            title: 'Type',
            dataIndex: 'input_type',
            key: 'input_type',
            render: (text) => (
                <Tag color={text === 'approval' ? 'blue' : 'green'}>
                    {text.charAt(0).toUpperCase() + text.slice(1)}
                </Tag>
            )
        },
        {
            title: 'Content',
            dataIndex: 'content',
            key: 'content',
            render: (content) => {
                if (typeof content === 'object') {
                    return JSON.stringify(content).substring(0, 100) + '...';
                }
                return content?.substring(0, 100) + '...';
            }
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => new Date(text).toLocaleString()
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, task) => (
                <Button onClick={() => {
                    setSelectedTask(task);
                    setModifiedContent(task.content);
                    setComments('');
                    setModalVisible(true);
                }}>
                    View & Action
                </Button>
            )
        }
    ];

    const renderContent = (content) => {
        if (!content) return null; // Return null if no instructions
    
        if (typeof content === 'object') {
            return JSON.stringify(content);
        }
        
        return (
            <div style={{ 
                marginBottom: 16,
                padding: 16,
                background: '#f5f5f5',
                borderRadius: 4
            }}>
                <div dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(content)
                }} />
            </div>
        );
    };

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>My Tasks</Title>
            
            <Table 
                columns={columns} 
                dataSource={tasks} 
                rowKey="id" 
                loading={loading}
            />

            <Modal
                title={`Task: ${selectedTask?.workflow_name}`}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={800}
            >
                {selectedTask && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                            <Text strong>Type: </Text>
                            <Tag color={selectedTask.input_type === 'approval' ? 'blue' : 'green'}>
                                {selectedTask.input_type.charAt(0).toUpperCase() + selectedTask.input_type.slice(1)}
                            </Tag>
                        </div>

                        {selectedTask.input_type === 'modification' ? (
                            <>
                            <div>
                                {selectedTask.instructions && (
                                    <>
                                        <Text strong>Instructions:</Text>
                                        {renderContent(selectedTask.instructions)}
                                    </>
                                )}
                                <Text strong>Original Content:</Text>
                                <div style={{ 
                                    marginBottom: 16,
                                    padding: 16,
                                    background: '#f5f5f5',
                                    borderRadius: 4
                                }}>
                                    <div dangerouslySetInnerHTML={{ 
                                        __html: DOMPurify.sanitize(selectedTask.content)
                                    }} />
                                </div>
                            </div>
                            <div>
                                <Text strong>Modified Content:</Text>
                                <TextArea
                                    rows={6}
                                    value={modifiedContent}
                                    onChange={(e) => setModifiedContent(e.target.value)}
                                    style={{ marginBottom: 16 }}
                                />
                            </div>
                        </>
                        ) : (
                            <div>
                                <Text strong>Content to Review:</Text>
                                <div style={{ 
                                    marginBottom: 16,
                                    padding: 16,
                                    background: '#f5f5f5',
                                    borderRadius: 4
                                }}>
                                    <div dangerouslySetInnerHTML={{ 
                                        __html: DOMPurify.sanitize(selectedTask.content)
                                    }} />
                                </div>
                            </div>
                        )}

                        <Space>
                            {selectedTask.input_type === 'approval' ? (
                                <>
                                    <Button 
                                        type="primary" 
                                        onClick={() => handleTaskAction(selectedTask, 'approve')}
                                    >
                                        Approve
                                    </Button>
                                    <Button 
                                        onClick={() => handleTaskAction(selectedTask, 'revert')}
                                    >
                                        Revert
                                    </Button>
                                    <Button 
                                        danger
                                        onClick={() => handleTaskAction(selectedTask, 'reject')}
                                    >
                                        Reject
                                    </Button>
                                </>
                            ) : (
                                <Button 
                                    type="primary"
                                    onClick={() => handleTaskAction(selectedTask, 'modify')}
                                >
                                    Submit Modification
                                </Button>
                            )}
                        </Space>
                    </Space>
                )}
            </Modal>
        </div>
    );
};

export default TasksPage;